import React, { memo, useEffect, useState } from 'react';

import {
  Space,
  Text,
  Icon,
  Popup,
} from '@cashfree-intl/coherent';

import CustomIcon from 'components/CustomIcons';
import Formik from 'components/Formik';
import { formatStringForDisplay } from 'utils/common';
import {
  UpfrontTDRFormTopSection,
  UpfrontTDRFormBottomSection,
  HighlighterDiv,
  StyledToolTipDivider,
} from './styled';

import { getUpfrontTDRTooltipText, preparePayload, preparePayloadV2, preparePaymentMethodOptions, validate } from '../helpers';
import { Option } from '../types';
import { INITIAL_VALUES } from '../constants';

import TDRForm from './TDRForm';
import TDRFormV2 from './TDRFormV2';

interface ITDRListProps {
  paymentMode: string;
  formData: any;
  showSlabs: boolean;
  gateways: any[];
  navigateBack: () => void;
  paymentMethods: Option[];
  setPaymentMethods: (data: Option[]) => void;
  getPaymentMethods: (data: any) => void;
  setFormData: (data: any) => void;
  saveUpfrontTDR: (data: any, actions: any) => void;
  enableSlabsForAll: boolean;
}

function TDRFormWrapper({
  paymentMode,
  formData,
  navigateBack,
  getPaymentMethods,
  paymentMethods,
  saveUpfrontTDR,
  setFormData,
  setPaymentMethods,
  showSlabs,
  gateways,
  enableSlabsForAll,
}: ITDRListProps) {
  const [paymentNetwork, setPaymentNetwork] = useState<any[]>([]);

  // if formData.length === 0 -> only 1 element is being updated
  // else -> more than 1 element is being updated
  let initialValues = { ...INITIAL_VALUES };

  if (formData.length === 1) {
    const { UpfrontTdr, ceil, floor, fixedFee, pg } = formData[0];

    initialValues.tdr = String(UpfrontTdr || '');
    initialValues.ceil = ceil;
    initialValues.floor = floor;
    initialValues.fixedFee = fixedFee;
    initialValues.pg = pg;
  } else if (formData.length > 1) {}

  const showPaymentNetwork = ['DEBIT_CARD', 'CREDIT_CARD'].includes(paymentMode);

  useEffect(() => {
    if (!showPaymentNetwork) {
      setTimeout(() => {
        getPaymentMethods({
          paymentMode,
          cb: response => {
            if (response.length > 0) {
              const result = preparePaymentMethodOptions(response);
              setPaymentMethods(result);
            } else {
              setPaymentMethods([]);
            }
          },
        });
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (showPaymentNetwork && paymentNetwork?.length > 0) {

      const filteredNetwork = paymentNetwork.filter(obj => obj.value !== 'ALL');
      const network = filteredNetwork.map(obj => obj.value);

      getPaymentMethods({
        paymentMode,
        network,
        // queryString: { network: network.join('&network=') },
        queryString: {},
        cb: response => {
          if (response.length > 0) {
            const result = preparePaymentMethodOptions(response);
            setPaymentMethods(result);
          } else {
            setPaymentMethods([]);
          }
        },
      });
      // }, 0);
    }
  }, [paymentNetwork]);

  const onSubmit = (values, actions) => {
    let payload;

    if (formData.length > 0) {
      // formData.length > 0 -> edit mode
      // we take out the values which are editable and just update for that modeCode
      if (showSlabs) {
        // here we need to create the payload by cloning and updating the form data
        payload = [{
          tdr: +values.tdr,
          tdrType: values.tdrType,
          floor: +values.floor,
          ceil: +values.ceil,
          fixedFee: +values.fixedFee,
          pg: values.pg,
        }];
      } else payload = [{ tdr: +values.tdr, tdrType: values.tdrType }];
    } else {
      // formData.length === 0 -> create mode
      payload = showSlabs ? preparePayloadV2(values) : preparePayload(values);
    }
    // make the api call here
    saveUpfrontTDR(payload, actions);
  }

  const HelpTooltipContent = () => <div>
    <div>
      <Text variant="b12" className="mb-1">
        Add Upfront TDR
      </Text>
      <Text variant="b12" color="bodyLight">
        1. {getUpfrontTDRTooltipText(paymentMode)}
      </Text>
      <Text variant="b12" color="bodyLight">
        2. Add a flat or percentage TDR, based on your choice, and click Save
      </Text>
      <Text variant="b12" color="bodyLight">
        3. TDR/Surcharge is added
      </Text>
    </div>
    <StyledToolTipDivider className='my-1' />
    <div>
      <Text variant="b12" className="mb-1">
        Update Upfront TDR
      </Text>
      <Text variant="b12" color="bodyLight">
        1. From the table at the bottom of this page, select rows on which you want to edit surcharge/TDR.
      </Text>
      <Text variant="b12" color="bodyLight">
        2. Click 'Update', you will redirected to the top of the page.
      </Text>
      <Text variant="b12" color="bodyLight">
        3. Update the surcharge according to your choice.
      </Text>
    </div>
  </div>

  return (
    <div className="mb-3">
      <Space
        alignItems="center"
        gap={0.5}
        onClick={navigateBack}
        className="pointer mb-4"
      >
        <Icon name="chevron-left" />
        <Text variant="b14" strong color="primary">
          Back
        </Text>
      </Space>
      {/* check if the formData is set then highlight the below div */}
      <HighlighterDiv highlight={formData && formData.length > 0}>
        <UpfrontTDRFormTopSection alignItems="center" className="p-3" gap={2}>
          <Text variant="h16" strong>
            Add/Update TDR on {formatStringForDisplay(paymentMode)}
          </Text>
          <Popup
            content={HelpTooltipContent}
            trigger={
              <span className='pointer'>
                <CustomIcon componentName="HELP" />
              </span>
            }
            position="right center"
          />
        </UpfrontTDRFormTopSection>
        <UpfrontTDRFormBottomSection className="p-3">
          <Formik
            initialValues={initialValues}
            validate={validate(formData.length > 0, showSlabs, enableSlabsForAll)}
            onSubmit={onSubmit}
            enableReinitialize
            customProps={{
              formData,
              setPaymentNetwork,
              paymentMethods,
              setFormData,
              showPaymentNetwork,
              gateways,
              enableSlabsForAll,
            }}
            children={showSlabs || enableSlabsForAll ? TDRFormV2 : TDRForm}
          >
          </Formik>
        </UpfrontTDRFormBottomSection>
      </HighlighterDiv>
    </div>
  );
}

export default memo(TDRFormWrapper);
